import React, { useState, useEffect } from 'react';
import { Activity as ActivityIcon, FileText, GitCommit, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ActivityTab = ({ entityType, entityId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const limit = 20;

  useEffect(() => {
    if (entityId) {
      fetchActivities(0);
    }
  }, [entityId, entityType]);

  const fetchActivities = async (skipCount = skip) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/activity/${entityType}/${entityId}?limit=${limit}&skip=${skipCount}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        if (skipCount === 0) {
          setActivities(data);
        } else {
          setActivities(prev => [...prev, ...data]);
        }
        
        setHasMore(data.length === limit);
        setSkip(skipCount + data.length);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
      toast.error('Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    fetchActivities(skip);
  };

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'ENTITY_CREATED':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'STATUS_CHANGED':
        return <TrendingUp className="h-4 w-4 text-orange-500" />;
      default:
        return <GitCommit className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-4" data-testid="activity-tab">
      <h3 className="text-sm font-semibold text-foreground">
        Activity Timeline
      </h3>

      {activities.length === 0 && !loading ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="empty-activity-state">
          <ActivityIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>No activity yet</p>
          <p className="text-sm">Actions will appear here as they happen</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity, index) => (
            <div
              key={activity.id}
              className="relative pl-8 pb-4"
              data-testid={`activity-${activity.id}`}
            >
              {/* Timeline line */}
              {index < activities.length - 1 && (
                <div className="absolute left-2 top-6 bottom-0 w-0.5 bg-border" />
              )}
              
              {/* Activity icon */}
              <div className="absolute left-0 top-0 w-5 h-5 rounded-full bg-card border-2 border-border flex items-center justify-center">
                {getActivityIcon(activity.activity_type)}
              </div>

              {/* Activity content */}
              <div className="bg-card border border-border rounded-lg p-3 hover:bg-muted/50 transition-colors">
                <p className="text-sm text-foreground font-medium mb-1">
                  {activity.summary}
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{activity.created_by_name}</span>
                  <span>•</span>
                  <span>{formatDate(activity.created_at)}</span>
                </div>
                
                {/* Additional data (if any) */}
                {activity.data && Object.keys(activity.data).length > 0 && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    {activity.data.old_status && activity.data.new_status && (
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-muted rounded">
                          {activity.data.old_status}
                        </span>
                        <span>→</span>
                        <span className="px-2 py-1 bg-primary/10 rounded">
                          {activity.data.new_status}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load More Button */}
      {hasMore && (
        <div className="flex justify-center pt-4">
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={loading}
            data-testid="load-more-button"
          >
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ActivityTab;
